<template>
  <div>
    <div
      v-if="postingAvailability"
      class="bg-tertiary text-on-tertiary rounded-t-lg p-4"
    >
      {{ postingAvailability }}
    </div>
    <div
      class="bg-surface rounded-b-lg p-4"
      :class="{ 'rounded-t-lg': !postingAvailability }"
    >
      <div
        class="flex flex-col lg:flex-row lg:justify-between lg:items-center flex-wrap gap-3"
      >
        <SocialTypeInfo :social-type="props.social.type" />
        <div class="flex gap-3 flex-wrap justify-between">
          <div class="flex gap-3">
            <IconButton
              v-if="singleSocialFileUrl && props.social.showDownloadButton"
              :icon-name="IconName.DOWNLOAD"
              label="Download"
              :variant="ButtonVariant.OUTLINED"
              :color="ButtonColor.SECONDARY"
              @click="downloadContentFile(props.social)"
            />
          </div>
          <Menu :items="postMenuItems" align="end">
            <template #trigger>
              <Button
                :label="$t('content.post')"
                :right-icon-name="IconName.CHEVRON_DOWN"
              />
            </template>
          </Menu>
          <EditPostModal
            v-if="postModalSchedule !== null"
            :social-data="props.social"
            :hub="props.hub"
            :content="props.content"
            :content-data="{
              hubSlug: props.hub?.slug,
              contentSlug: props.content?.slug,
              schedule: postModalSchedule,
            }"
            @close="postModalSchedule = null"
          />
        </div>
      </div>
      <hr class="mt-5" />
      <div
        v-if="!props.social.isCustomizable"
        class="mt-5 whitespace-pre-wrap truncate ellipsis"
      >
        {{ props.social.description }}
      </div>
      <div v-else class="mt-5">
        <Description
          :description="props.social.description"
          enable-values
          @update:error-state="descriptionErrorState = $event"
        />
      </div>
      <template v-if="props.social.socialFiles.length > 0">
        <template v-if="carouselFiles.length > 1">
          <div class="relative mt-5">
            <CarouselGridPreviewModal
              :social-files="social.socialFiles"
              :social-type="props.social.type"
            >
              <Carousel
                :variant="socialTypeCarousel[props.social.type]"
                :files="carouselFiles"
                :post-data="props.social"
                class="flex-grow"
              />
            </CarouselGridPreviewModal>
            <div class="absolute top-2 left-2 flex items-center gap-2 z-10">
              <Chip
                v-if="social.isCustomizable"
                :size="ChipSize.SM"
                :variant="ChipVariant.FILLED"
                :icon="IconName.SETTINGS"
                :color="ChipColor.SECONDARY"
              >
                {{ $t('content.customizable') }}
              </Chip>
            </div>
          </div>
        </template>
        <div
          v-else-if="isFileImage(singleSocialFileUrl)"
          class="aspect-square mt-5 w-full relative overflow-hidden"
        >
          <img
            :src="singleSocialFileUrl"
            class="rounded-md object-contain w-full h-full relative z-10"
            alt="Image File"
          />
          <div
            class="absolute -top-5 -left-5 w-[110%] h-[110%] z-0"
            :style="`background-image: url(${singleSocialFileUrl}); filter: blur(10px);`"
          />
          <div class="absolute bottom-2 left-2 flex items-center gap-2 z-10">
            <Chip
              v-if="social.isCustomizable"
              :size="ChipSize.SM"
              :variant="ChipVariant.FILLED"
              :icon="IconName.SETTINGS"
              :color="ChipColor.SECONDARY"
            >
              {{ $t('content.customizable') }}
            </Chip>
          </div>
        </div>
        <div
          v-else-if="isFileVideo(singleSocialFileUrl)"
          class="w-full rounded-md aspect-video mt-5"
        >
          <video
            :src="singleSocialFileUrl"
            controls
            class="w-full h-full rounded-md post-video"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Button,
  ButtonColor,
  ButtonVariant,
  IconButton,
  IconName,
  Menu,
  type MenuEntry,
} from '@exfluential/ui';
import {
  apiCreateContentSocialDraft,
  apiCreateSocialShareUrl,
  apiCreateSocialShareUrlFromDraft,
  apiGetContentSocialDraftsList,
  ContentDto,
  ContentSocialType,
  CreateContentSocialDraftBodyDto,
} from '~/api/contents';
import { buildSocialFileDownloadName, downloadFile } from '~/utils/file';
import {
  useSocialRedirectUrl,
  navigateToSocialShareUrl,
  navigateTo,
  useRoute,
} from '#imports';
import {
  ContentMetricType,
  createContentLaunchMetric,
  createContentMetric,
  LaunchMetricType,
} from '~/api/metrics';
import { useLoader } from '~/composables/use-loader';
import { SocialTypeInfo } from '~/pages/_components/social-type-info';
import { EditPostModal } from '~/pages/content/my-posts/_components/edit-post-modal';
import { useAuthUserStore } from '~/store/auth-user';
import { Description } from '../../../../../description';
import { useCustomDescription } from '~/composables/use-custom-description';
import { Nullable } from '~/types/common';
import { Chip, ChipColor, ChipSize, ChipVariant } from '~/components/chip';
import { useVariablesModalStore } from '~/store/variables-modal';
import { AccountType } from '~/api/accounts';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '~/store/account';
import { HubDto } from '~/api/hubs';
import { Carousel } from '~/components/carousel';
import { socialTypeCarousel } from '~/constants/social-posts';
import { CarouselGridPreviewModal } from '~/pages/_components/content-details/_components/socials-area/_components/social-preview/_components/carousel-grid-preview-modal';
import { format, isFuture, parseISO } from 'date-fns';

type Props = {
  hub?: HubDto;
  content: ContentDto;
  social: NonNullable<ContentDto['socials']>[number];
  displayPostLaterAndEdit: boolean;
};
const props = defineProps<Props>();

const emit = defineEmits<{
  complete: [];
}>();

const route = useRoute();

const { t } = useI18n();

const descriptionErrorState = ref(null);

const authUser = useAuthUserStore();
const { currentAccount } = storeToRefs(useAccountStore());

const { setSubmitCallback } = useVariablesModalStore();
const { validateVariables, getCustomDescription, activateVariablesModal } =
  useCustomDescription(props.social.description);

const socialDownloadMetricMap: Record<ContentSocialType, ContentMetricType> = {
  // [ContentSocialType.TWITTER]: ContentMetricType.TWITTER_POST_DOWNLOAD,
  [ContentSocialType.FACEBOOK]: ContentMetricType.FACEBOOK_POST_DOWNLOAD,
  [ContentSocialType.LINKEDIN]: ContentMetricType.LINKEDIN_POST_DOWNLOAD,
  [ContentSocialType.INSTAGRAM]: ContentMetricType.INSTAGRAM_POST_DOWNLOAD,
};

const singleSocialFileUrl = computed(
  () => props.social.socialFiles[0]?.file?.url,
);

const isPostEditable = computed<boolean>(() => {
  return (
    (props.social.isEditable || props.social.isCustomizable) &&
    (authUser.isLoggedIn || props.displayPostLaterAndEdit)
  );
});

const handleDraftSave = async (): Promise<number | undefined> => {
  const socialBody: CreateContentSocialDraftBodyDto = {
    contentSocialId: props.social.id,
    finishedAt: new Date().toISOString(),
    logos: [],
    description: props.social.description,
    socialType: props.social.type,
  };

  if (props.social.isEditable) {
    socialBody.description = props.social.description;
  }

  const draft = await apiCreateContentSocialDraft(socialBody);
  return draft.id;
};

const downloadContentFile = async (socialPost: Props['social']) => {
  if (props.hub) {
    createContentMetric(
      props.hub.slug,
      props.content.slug,
      socialDownloadMetricMap[props.social.type],
    );
  }

  const downloads = await Promise.all(
    socialPost.socialFiles.map(({ file }, index, files) =>
      downloadFile(
        file.url,
        buildSocialFileDownloadName(
          props.content.title,
          file.url,
          index,
          files.length,
        ),
      ),
    ),
  );
  if (!downloads.every(Boolean)) return;

  if (
    props.hub?.isLaunchEnabled &&
    currentAccount.value?.type === AccountType.STANDARD &&
    !props.content.launchData.completedAt
  ) {
    await createContentLaunchMetric(
      props.hub.slug,
      props.content.slug,
      LaunchMetricType.COMPLETED,
    );
    emit('complete');
  }
};

const getSocialShareUrl = async (
  description?: string,
  newDraftId?: number,
): Promise<Nullable<string>> => {
  let draftId: number | undefined = newDraftId;

  if (!draftId && props.social.isCustomizable) {
    const { nodes: drafts } = await apiGetContentSocialDraftsList({
      page: 1,
      perPage: 100,
    });

    const existingDraft = drafts.find(
      (d) => d.contentSocial.id === props.social.id && d.logos.length > 0,
    );

    if (existingDraft) {
      draftId = existingDraft.id;
    }
  }

  if (props.hub) {
    const { shareUrl } = await apiCreateSocialShareUrl(
      props.hub.slug,
      props.content.slug,
      {
        contentSocialId: props.social.id,
        description,
        socialType: props.social.type,
        ...(draftId && { draftId }),
      },
    );

    return shareUrl;
  }

  const payload = {
    contentSocialId: props.social.id,
    hubSlug: props.hub?.slug,
    contentSlug: props.content.slug,
    description,
    socialType: props.social.type,
    ...(draftId && { draftId }),
    socialFiles: props.social.socialFiles.map(({ file, caption }) => ({
      caption: caption || null,
      fileId: file.id,
    })),
  };

  const { shareUrl } = await apiCreateSocialShareUrlFromDraft(payload);

  return shareUrl;
};

const handleSocialShareRedirectWithDraft = async (draftId?: number) => {
  const description = getCustomDescription();
  const shareUrl = await getSocialShareUrl(description, draftId);

  const { updateSocialRedirectUrl } = useSocialRedirectUrl();

  updateSocialRedirectUrl();

  if (shareUrl) {
    if (
      props.hub?.isLaunchEnabled &&
      currentAccount.value?.type === AccountType.STANDARD &&
      !props.content.launchData.completedAt
    ) {
      await createContentLaunchMetric(
        props.hub.slug,
        props.content.slug,
        LaunchMetricType.COMPLETED,
      );
      // eslint-disable-next-line vue/no-mutating-props
      props.content.launchData.completedAt = new Date();
    }

    navigateToSocialShareUrl(shareUrl);
  }
};

const handleSocialPost = async () => {
  await useLoader({
    action: async () => {
      let newDraftId: number | undefined;

      if (props.social.isCustomizable) {
        newDraftId = await handleDraftSave();
      }

      if (descriptionErrorState.value) {
        const isDescriptionValid = validateVariables(
          descriptionErrorState.value,
        );

        if (!isDescriptionValid) {
          setSubmitCallback(() =>
            handleSocialShareRedirectWithDraft(newDraftId),
          );
          activateVariablesModal(descriptionErrorState.value);
          return null;
        }
      }

      await handleSocialShareRedirectWithDraft(newDraftId);
    },
  });
};

const postModalSchedule = ref<boolean | null>(null);

const activateModal = (schedule: boolean) => {
  if (authUser.isLoggedIn) {
    postModalSchedule.value = schedule;
  } else {
    navigateTo({
      path: '/auth/login',
      query: { redirect: route.fullPath },
    });
  }
};

const handlePostNow = async () => {
  if (props.social.isEditable) {
    activateModal(false);
    return;
  }
  await handleSocialPost();
};

const carouselFiles = computed(() =>
  props.social.socialFiles.map(({ file }) => ({
    id: file.id,
    src: file.url,
  })),
);

const postingAvailability = computed<string | null>(() => {
  const formatPattern = 'MMMM d, yyyy';
  const visibilityEndDate = props.content?.endDate ?? null;
  const availabilityStartDate = props.social.isAvailabilityDateEnabled
    ? props.social.availabilityStartDate
    : null;

  if (!visibilityEndDate) {
    if (availabilityStartDate !== null && isFuture(availabilityStartDate)) {
      return t('content.post-availability.starting', {
        date: format(parseISO(availabilityStartDate), formatPattern),
      });
    }

    return null;
  }

  if (availabilityStartDate !== null && isFuture(availabilityStartDate)) {
    return t('content.post-availability.range', {
      start: format(parseISO(availabilityStartDate), formatPattern),
      end: format(parseISO(visibilityEndDate), formatPattern),
    });
  }

  return t('content.post-availability.until', {
    date: format(parseISO(visibilityEndDate), formatPattern),
  });
});

const isAvailabilityStartInFuture = computed<boolean>(() => {
  return (
    props.social.isAvailabilityDateEnabled &&
    props.social.availabilityStartDate !== null &&
    isFuture(props.social.availabilityStartDate)
  );
});

const postMenuItems = computed<MenuEntry[]>(() => [
  {
    label: t('content.post-now'),
    disabled: isAvailabilityStartInFuture.value,
    onSelect: handlePostNow,
  },
  ...(authUser.isLoggedIn || props.displayPostLaterAndEdit
    ? [
        {
          label: t('content.post-later'),
          onSelect: () => activateModal(true),
        },
      ]
    : []),
  ...(isPostEditable.value
    ? [
        {
          label: props.social.isCustomizable
            ? t('common.personalize')
            : t('common.edit'),
          onSelect: () => activateModal(isAvailabilityStartInFuture.value),
        },
      ]
    : []),
]);
</script>

<style scoped>
@reference "../../../../../../../assets/css/main.css";

.post-video {
  background: #000000;
}
</style>
